import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Copy, ShieldAlert } from "lucide-react";
import { mfaApi } from "../../services/mfaApi";
import { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { useLocalization } from "../../hooks/useLocalization";
import { getLandingRouteForUser } from "../../lib/roleRouting";
import { LanguageSwitcher } from "../../components/LanguageSwitcher";

export default function MfaSetupPage() {
  const { t } = useLocalization();
  const { applyAuthenticatedUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const setupToken = location.state?.setupToken;

  const [qrData, setQrData] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    if (!setupToken) return;
    mfaApi.setupInit(setupToken).then((r) => setQrData(r.data)).catch(() => setError(t("mfa.sessionExpired")));
  }, [setupToken, t]);

  if (!setupToken) {
    return (
      <div className="min-h-screen bg-blue-950 flex items-center justify-center p-6" data-testid="mfa-setup-expired">
        <div className="bg-white rounded-2xl p-8 max-w-sm text-center shadow-2xl">
          <ShieldAlert className="mx-auto text-red-500 mb-3" size={40} />
          <p className="text-sm text-gray-600 mb-6">{t("mfa.sessionExpired")}</p>
          <Link to="/staff/login" className="text-sm font-semibold text-blue-600 hover:underline">{t("mfa.backToLogin")}</Link>
        </div>
      </div>
    );
  }

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await mfaApi.setupVerify(setupToken, code);
      setBackupCodes(data.backup_codes);
      setPendingUser(data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t("mfa.invalidCode"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = () => {
    applyAuthenticatedUser(pendingUser);
    const target = pendingUser.must_change_password ? "/change-password" : getLandingRouteForUser(pendingUser);
    navigate(target, { replace: true });
  };

  const handleCopyAll = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success(t("mfa.codesCopied"));
  };

  return (
    <div className="min-h-screen bg-blue-950 flex items-center justify-center p-6" data-testid="mfa-setup-page">
      <div className="absolute top-6 right-6"><LanguageSwitcher /></div>
      <div className="w-full max-w-md bg-white rounded-2xl p-8 shadow-2xl">
        {!backupCodes ? (
          <>
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="text-blue-600" size={28} />
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">{t("mfa.setupTitle")}</h1>
            </div>
            <p className="text-sm text-gray-500 mb-6">{t("mfa.setupSubtitle")}</p>

            {qrData ? (
              <>
                <div className="flex justify-center mb-4">
                  <img src={qrData.qr_code_data_url} alt="MFA enrollment QR code" className="h-44 w-44" data-testid="mfa-qr-code" />
                </div>
                <div className="bg-gray-50 rounded-lg p-3 mb-6 text-center">
                  <p className="text-xs text-gray-500 mb-1">{t("mfa.manualKeyLabel")}</p>
                  <p className="font-mono text-sm font-semibold tracking-wider text-gray-800" data-testid="mfa-manual-key">{qrData.manual_setup_key}</p>
                </div>
                <form onSubmit={handleVerify} className="space-y-4">
                  <div>
                    <label htmlFor="mfa-setup-code" className="text-sm font-medium text-gray-700 mb-1 block">{t("mfa.enterCodeLabel")}</label>
                    <input
                      id="mfa-setup-code"
                      data-testid="mfa-setup-code-input"
                      type="text"
                      required
                      maxLength={6}
                      pattern="[0-9]{6}"
                      inputMode="numeric"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {error && <p className="text-sm text-red-500" data-testid="mfa-setup-error">{error}</p>}
                  <button
                    data-testid="mfa-setup-verify-button"
                    type="submit"
                    disabled={submitting || code.length !== 6}
                    className="w-full py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
                  >
                    {submitting ? t("common.loading") : t("mfa.verifyAndActivate")}
                  </button>
                </form>
              </>
            ) : (
              <p className="text-sm text-gray-500" data-testid="mfa-setup-loading">{t("common.loading")}</p>
            )}
          </>
        ) : (
          <div data-testid="mfa-backup-codes-panel">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 mb-2">{t("mfa.backupCodesTitle")}</h1>
            <p className="text-sm text-gray-500 mb-4">{t("mfa.backupCodesSubtitle")}</p>
            <div className="bg-gray-50 rounded-lg p-4 mb-4 grid grid-cols-2 gap-2 font-mono text-sm text-gray-800" data-testid="mfa-backup-codes-list">
              {backupCodes.map((c) => <span key={c}>{c}</span>)}
            </div>
            <button
              data-testid="mfa-copy-backup-codes-button"
              onClick={handleCopyAll}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 active:scale-95 transition-all mb-4"
            >
              <Copy size={14} /> {t("mfa.copyAllCodes")}
            </button>
            <label className="flex items-center gap-2 mb-5 text-sm text-gray-700">
              <input
                data-testid="mfa-saved-codes-checkbox"
                type="checkbox"
                checked={savedConfirmed}
                onChange={(e) => setSavedConfirmed(e.target.checked)}
                className="rounded border-gray-300"
              />
              {t("mfa.savedCodesConfirm")}
            </label>
            <button
              data-testid="mfa-continue-button"
              disabled={!savedConfirmed}
              onClick={handleContinue}
              className="w-full py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
            >
              {t("mfa.continueBtn")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
